import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Video,
  Images,
  ArrowRight,
  ScrollText,
  Plug,
  AlertTriangle,
  Sparkles,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drive, setDrive] = useState({ connected: false });
  const [uploadpost, setUploadpost] = useState({ configured: false, accounts: [] });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, u, us] = await Promise.all([
          api.get("/drive/status"),
          api.get("/integrations/uploadpost"),
          api.get("/usage/today").catch(() => ({ data: null })),
        ]);
        setDrive(d.data);
        setUploadpost(u.data);
        setUsage(us.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const anyLinked = (uploadpost.accounts || []).some(
    (a) => a.tiktok_connected || a.instagram_connected
  );
  const setupIncomplete = !loading && (!drive.connected || !uploadpost.configured || !anyLinked);
  const missing = [];
  if (!drive.connected) missing.push("Google Drive");
  if (!uploadpost.configured) missing.push("Publishing key");
  else if (!anyLinked) missing.push("A linked TikTok or Instagram account");
  const tier = usage?.tier || "none";
  const noPlan = tier === "none";
  const onTrial = !!usage?.on_trial;
  const unlimited = !!usage?.unlimited;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="relative overflow-hidden rounded-xl border border-zinc-200" data-testid="dashboard-hero">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1550029402-226115b7c579?crop=entropy&cs=tinysrgb&fm=jpg&q=80&w=1600"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-600/85 via-fuchsia-500/60 to-cyan-500/70" />
        <div className="relative px-5 py-8 sm:px-8 sm:py-12 text-white">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-white/80">Console</div>
          <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl drop-shadow-lg">
            What are we posting today?
          </h1>
          <p className="mt-1.5 text-sm sm:text-base text-white/90 drop-shadow">
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — pick a format to get started.
          </p>
        </div>
      </header>

      {setupIncomplete && (
        <Card className="p-4 sm:p-5 border-amber-200 bg-amber-50 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="setup-incomplete-card">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="shrink-0 rounded-full bg-amber-100 p-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-amber-900">Finish setup first</div>
              <p className="mt-0.5 text-sm text-amber-900/80">
                Missing: {missing.join(", ")}. Publishing needs these before your first post.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/integrations")}
            className="bg-amber-900 hover:bg-amber-800 text-white shrink-0"
            data-testid="setup-cta"
          >
            Open Integrations <ArrowRight className="w-4 h-4" />
          </Button>
        </Card>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="post-format-tiles">
        <ActionTile
          testId="tile-video"
          icon={<Video className="w-6 h-6 text-blue-600" />}
          eyebrow="Format"
          title="Post a Video"
          description="Send a single MP4 (or a small batch) from Drive to TikTok or Instagram Reels."
          cta="Open Video Browser"
          onClick={() => navigate("/drive?mode=video")}
          accentClass="from-blue-500/10 to-blue-500/0"
          disabled={setupIncomplete}
        />
        <ActionTile
          testId="tile-carousel"
          icon={<Images className="w-6 h-6 text-fuchsia-600" />}
          eyebrow="New"
          title="Post a Carousel"
          description="Pick multiple photos from Drive to publish as a TikTok slideshow or Instagram carousel."
          cta="Open Carousel Browser"
          onClick={() => navigate("/drive?mode=carousel")}
          accentClass="from-fuchsia-500/10 to-fuchsia-500/0"
          disabled={setupIncomplete}
          badge={<span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-700 bg-fuchsia-100 rounded-full px-2 py-0.5"><Sparkles className="w-3 h-3" /> New</span>}
        />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 border-zinc-200" data-testid="plan-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Plan</div>
              <div className="mt-1 font-display text-2xl font-bold text-slate-900 capitalize">
                {unlimited ? "Admin — Unlimited" : noPlan ? "No active plan" : tier}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {unlimited
                  ? "You have full access to every feature. No quotas, no billing."
                  : onTrial
                    ? "3-day trial — pick a paid plan before it ends to keep publishing."
                    : noPlan
                      ? "Start a plan to unlock posting."
                      : `Daily limits reset at 00:00 UTC.`}
              </p>
            </div>
            {unlimited && <Badge className="bg-fuchsia-100 text-fuchsia-800 hover:bg-fuchsia-100"><Sparkles className="w-3 h-3" /> Admin</Badge>}
            {onTrial && !unlimited && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100"><Sparkles className="w-3 h-3" /> Trial</Badge>}
          </div>
          {usage && !noPlan && !unlimited && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <UsageBar icon={<Video className="w-3.5 h-3.5" />} label="Videos" used={usage.videos_used} limit={usage.videos_limit} testId="usage-videos" />
              <UsageBar icon={<Images className="w-3.5 h-3.5" />} label="Carousels" used={usage.carousels_used} limit={usage.carousels_limit} testId="usage-carousels" />
            </div>
          )}
          {!unlimited && (
            <Button
              onClick={() => navigate("/pricing")}
              className={`mt-5 ${noPlan ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`}
              variant={noPlan ? "default" : "outline"}
              data-testid="cta-pricing"
            >
              <Zap className="w-4 h-4" /> {noPlan ? "Choose a plan" : "Manage plan"}
            </Button>
          )}
        </Card>

        <Card className="p-6 border-zinc-200">
          <ScrollText className="w-5 h-5 text-slate-700" />
          <div className="mt-4 font-display font-bold text-lg text-slate-900">Attempt Logs</div>
          <p className="mt-1 text-sm text-slate-600">Every send attempt with status, platform and response summary.</p>
          <Link to="/logs">
            <Button className="mt-4" variant="outline" data-testid="quick-logs">
              View Logs <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </Card>
      </section>

      <section>
        <Card className="p-6 border-zinc-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Plug className="w-5 h-5 text-slate-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-slate-900">Integrations</div>
            <p className="mt-1 text-sm text-slate-600">Manage Google Drive, TikTok and Instagram connections.</p>
          </div>
          <Link to="/integrations" className="shrink-0">
            <Button variant="outline" data-testid="quick-integrations">
              Open Integrations <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </Card>
      </section>
    </div>
  );
}

function UsageBar({ icon, label, used, limit, testId }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const nearFull = pct >= 80;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-[11px] text-slate-600">
        <span className="inline-flex items-center gap-1">{icon} {label}</span>
        <span className="tabular-nums font-medium text-slate-900">{used} / {limit}</span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${nearFull ? "bg-amber-500" : "bg-blue-600"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function ActionTile({ testId, icon, eyebrow, title, description, cta, onClick, accentClass, disabled, badge }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`group relative text-left rounded-xl border border-zinc-200 bg-white p-6 transition-all overflow-hidden ${
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:border-zinc-300 hover:shadow-md active:scale-[0.99]"
      }`}
      data-testid={testId}
    >
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accentClass}`} />
      <div className="relative">
        <div className="flex items-center justify-between gap-2">
          <div className="rounded-lg bg-zinc-100 p-2.5 w-fit">{icon}</div>
          {badge}
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{eyebrow}</div>
        <div className="mt-1 font-display text-2xl font-bold text-slate-900">{title}</div>
        <p className="mt-2 text-sm text-slate-600 max-w-md">{description}</p>
        <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 group-hover:gap-2 transition-all">
          {disabled ? "Finish setup first" : cta}
          {!disabled && <ArrowRight className="w-4 h-4" />}
        </div>
      </div>
    </button>
  );
}
